import { BoxProps, Checkbox, Grid, Tr, useMergeRefs } from "@chakra-ui/react";
import { MutableRefObject, forwardRef, useContext, useRef } from "react";
import { useNavigate } from "react-router";

import { ContextMenu } from "@nens/chakra/components";
import { useRect } from "@nens/react/hooks";
import { makeUnique } from "@nens/shared-utils";

import { useDrag } from "../../../hooks/use-drag";
import { MenuAction, RanaContextMenu } from "../../components/rana-context-menu";
import { DISABLED_OPACITY } from "../../theme/consts/other-consts";
import { SIZES } from "../../theme/consts/sizing-consts";
import { TableContext } from "../rana-table";

import { TableCell } from "./table-cell";
import { TableRowAccordion } from "./table-row-accordion";

export interface TableRowProps extends BoxProps {
  rowId: string;
  rowActions?: MenuAction[];
  isDisabled?: boolean;
  isSelected?: boolean;
  isHovered?: boolean;
  to?: string;
  queryParams?: string;
  isDraggable?: boolean;
  onDropInto?: (rowIds: string[]) => void;
}

export const TABLE_ROW_HEIGHT = SIZES[11];

const useRowOrSelectionActions = (props: TableRowProps) => {
  const { rowChecked, tableActions, checkedRows } = useContext(TableContext);
  const { rowId, rowActions } = props;

  if (rowChecked(rowId) && checkedRows.length > 1 && tableActions?.length)
    return tableActions.map(({ label, action, icon, getDisabledText }) => ({
      label,
      icon,
      disabledText: getDisabledText ? getDisabledText(checkedRows) : undefined,
      action: () => action(checkedRows),
    }));

  return rowActions;
};

export function TableRow(props: TableRowProps) {
  const { rowChecked, selectOnly } = useContext(TableContext);
  const { rowId, isDisabled } = props;
  const actions = useRowOrSelectionActions(props);

  return (
    <ContextMenu<HTMLDivElement>
      key={rowId}
      renderMenu={
        !isDisabled && actions?.length ? () => <RanaContextMenu.Actions actions={actions} /> : null
      }
      onOpen={() => {
        if (!rowChecked(rowId)) selectOnly(rowId);
      }}
    >
      {(ref) => <TableRowInner ref={ref} {...props} />}
    </ContextMenu>
  );
}

TableRow.Accordion = TableRowAccordion;

// If row touches cotainer it should not have a bottom line
const useTableRowBorderProps = (rowRef: MutableRefObject<HTMLDivElement | null>): BoxProps => {
  const { containerRef } = useContext(TableContext);
  const rowRect = useRect(rowRef, true);
  const conatinerRect = useRect(containerRef, true);

  if (Math.abs(conatinerRect.bottom - rowRect.bottom) < 5) return {};

  return { borderBottom: "1px solid", borderColor: "brand.border" };
};

const TableRowInner = forwardRef<HTMLDivElement, TableRowProps>((props, contextMenuRef) => {
  const hasRowOrSelectionActions = !!useRowOrSelectionActions(props)?.length;
  const {
    rowActions,
    rowId,
    isDisabled,
    isSelected,
    isHovered,
    to,
    queryParams,
    children,
    isDraggable,
    onDropInto,
    ...rest
  } = props;
  const { rowChecked, setChecked, hasSelection, templateColumns, checkedRows, onCheckboxClicked } =
    useContext(TableContext);
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const refs = useMergeRefs(rowRef, contextMenuRef);
  const rowBorderProps = useTableRowBorderProps(rowRef);

  const { dropProps, dragProps, isDraggingThisEl, isDraggingAnEl } = useDrag({
    ids: makeUnique([rowId, ...checkedRows]),
    dragId: "file-drag",
    onDropInto,
  });

  const isActuallySelected = isSelected || isDraggingThisEl || rowChecked(rowId);
  const isActuallyDisabled = ((!onDropInto || isActuallySelected) && isDraggingAnEl) || isDisabled;
  let rowBackground = "brand.bg";
  if (isHovered) rowBackground = "button.tertiary.hover";
  if (isActuallySelected) rowBackground = "brand.selected";

  const doesSomethingOnClick = rest.onClick || to || queryParams;
  const hasHover = hasRowOrSelectionActions || doesSomethingOnClick;

  return (
    <Grid
      ref={refs}
      as={Tr}
      minW="full"
      w="fit"
      bg={rowBackground}
      h={TABLE_ROW_HEIGHT}
      templateColumns={templateColumns}
      pl={4}
      {...rowBorderProps}
      {...rest}
      {...(isActuallyDisabled
        ? {
            onClick: undefined,
            _hover: {},
            cursor: "default",
            sx: { "*": { pointerEvents: "none" } },
            opacity: DISABLED_OPACITY,
          }
        : {
            onClick: (event) => {
              if (rest.onClick) rest.onClick(event);
              if (!to && !queryParams) return;
              const targetPath = `${to ?? window.location.pathname}${
                queryParams ? "?" + queryParams : ""
              }`;
              navigate(targetPath);
            },
            cursor: doesSomethingOnClick ? "pointer" : rest.cursor,
            _hover: hasHover
              ? {
                  ...rest._hover,
                  bg: isActuallySelected ? "brand.selected" : "button.tertiary.hover",
                }
              : rest._hover,
          })}
      {...(isDraggable ? { ...dropProps, ...dragProps } : {})}
    >
      {hasSelection ? (
        <TableCell
          cursor="pointer"
          onClick={(event) => {
            event.stopPropagation();
            const newRows = checkedRows.includes(rowId)
              ? checkedRows.filter((id) => id !== rowId)
              : [...checkedRows, rowId];
            setChecked(rowId, event);
            onCheckboxClicked && onCheckboxClicked(newRows);
          }}
        >
          <Checkbox isChecked={isActuallySelected} pointerEvents="none" />
        </TableCell>
      ) : null}
      {children}
      {rowActions?.length ? (
        <TableCell
          onClick={(event) => {
            event.stopPropagation();
          }}
          p={0}
          justifyContent="flex-end"
        >
          <RanaContextMenu actions={rowActions} />
        </TableCell>
      ) : null}
    </Grid>
  );
});

TableRowInner.displayName = "TableRowInner";
